import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Spinner
} from 'reactstrap';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import Header from '../../components/Headers/Header';
import { apiURL1 } from '../../config';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Footer from 'components/Footers/Footer';

const baseURL = apiURL1; // Replace with your actual base URL

function AddExam() {
    const token = localStorage.getItem("token");

    const navigate = useNavigate();
    const [classes, setClasses] = useState([]);
    const [selectedClass, setSelectedClass] = useState('');
    const [course, setCourse] = useState(null);
    const [subjects, setSubjects] = useState([]);
    const [examType, setExamType] = useState('');
    const [examYear, setExamYear] = useState(null);
    const [examSchedule, setExamSchedule] = useState([]);
    const [loading, setLoading] = useState(false);
    const [examTypes, setExamTypes] = useState([]);

    useEffect(() => {
        fetchClasses();
        fetchExamTypes();
    }, []);

    useEffect(() => {
        const fetchCourseAndSubjects = async () => {
            if (selectedClass) {
                try {
                    const classResponse = await axios.get(`${baseURL}/superadmin/oneClassForExam/${selectedClass}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`, // Include the token in the headers
                        }
                    });
                    setCourse(classResponse.data.data.course);

                    const courseResponse = await axios.get(`${baseURL}/superadmin/oneCourseForExam/${classResponse.data.data.course._id}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`, // Include the token in the headers
                        }
                    });
                    setSubjects(courseResponse.data.data.subjects);

                    // Initialize exam schedule with subjects
                    setExamSchedule(courseResponse.data.data.subjects.map(s => ({
                        subjectId: s.subject._id,
                        teacherId: s.teacher ? s.teacher._id : null,
                        examDate: dayjs().format('DD-MM-YYYY'),
                        startTime: '12:00 PM',
                        endTime: '03:00 PM',
                        subjectCode: s.subject.subjectCode,
                        subjectName: s.subject.name,
                        teacherName: s.teacher ? s.teacher.name : 'N/A'
                    })));
                } catch (error) {
                    console.error('Error fetching course and subjects:', error);
                    toast.error('Error fetching course and subjects');
                }
            } else {
                setCourse(null);
                setSubjects([]);
                setExamSchedule([]);
            }
        };

        fetchCourseAndSubjects();
    }, [selectedClass]);

    const fetchClasses = async () => {
        try {
            const response = await axios.get(`${baseURL}/superadmin/allClassForExam`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            setClasses(response.data.data);
        } catch (error) {
            console.error('Error fetching classes:', error);
            toast.error('Error fetching classes');
        }
    };

    const fetchExamTypes = async () => {
        try {
            const response = await axios.get(`${baseURL}/superadmin/examType`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            if (response.data && response.data.success && Array.isArray(response.data.data)) {
                setExamTypes(response.data.data);
            } else {
                setExamTypes([]);
                if (response.data && !response.data.success) {
                    toast.error(response.data.message || 'Failed to fetch exam types');
                }
            }
        } catch (error) {
            console.error('Error fetching exam types:', error);
            toast.error('Error fetching exam types');
            setExamTypes([]);
        }
    };

    const handleClassChange = (e) => {
        setSelectedClass(e.target.value);
    };

    const handleTypeChange = (e) => {
        setExamType(e.target.value);
    };

    const handleYearChange = (date) => {
        setExamYear(date);
    };

    const handleScheduleChange = (index, field, value) => {
        const newSchedule = [...examSchedule];
        newSchedule[index][field] = value;
        setExamSchedule(newSchedule);
    };

    const handleSubmit = async (e, status) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Validate that all fields are filled
            if (!selectedClass || !course || !examType || !examYear) {
                toast.error('Please fill in all the required fields.');
                setLoading(false);
                return;
            }

            // Validate exam schedule
            for (const item of examSchedule) {
                if (!item.examDate || !item.startTime || !item.endTime) {
                    toast.error('Please fill in all exam schedule details.');
                    setLoading(false);
                    return;
                }
            }

            const examDetails = examSchedule.map(item => ({
                subjectId: item.subjectId,
                teacherId: item.teacherId,
                examDate: item.examDate,
                startTime: item.startTime,
                endTime: item.endTime
            }));

            const payload = {
                classId: selectedClass,
                courseId: course._id,
                examSchedule: examDetails,
                type: examType,
                year: dayjs(examYear).year(),
                status: status
            };

            await axios.post(`${baseURL}/superadmin/exams`, payload, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            toast.success(`Exam ${status === 'draft' ? 'saved as draft' : 'published'} successfully!`);
            navigate('/admin/academic-progress-mangement');
        } catch (error) {
            console.error('Error adding exam:', error);
            if (error.response && error.response.status === 409) {
                toast.error('Exam already exists for this class, year, and type.');
            } else {
                toast.error('Error adding exam');
            }
        } finally {
            setLoading(false);
        }
    };



    return (
        <>
            <Header />
            <Container>
                <Row>
                    <Col>
                        <Card>
                            <CardHeader>
                                <h3>Add Exam</h3>
                            </CardHeader>
                            <CardBody>
                                <Form onSubmit={(e) => handleSubmit(e, 'draft')}>
                                    <FormGroup>
                                        <Label>Class</Label>
                                        <Input
                                            type="select"
                                            value={selectedClass}
                                            onChange={handleClassChange}
                                            required
                                        >
                                            <option value="">Select Class</option>
                                            {classes.map((cls) => (
                                                <option key={cls._id} value={cls._id}>
                                                    {cls.className}
                                                </option>
                                            ))}
                                        </Input>
                                    </FormGroup>

                                    {course && (
                                        <FormGroup>
                                            <Label>Course</Label>
                                            <Input type="text" value={course.courseName} readOnly />
                                        </FormGroup>
                                    )}

                                    <FormGroup>
                                        <Label>Exam Type</Label>
                                        <Input
                                            type="select"
                                            value={examType}
                                            onChange={handleTypeChange}
                                            required
                                        >
                                            <option value="">Select Exam Type</option>

                                            {examTypes.map((type) => (
                                                <option key={type._id} value={type._id}>
                                                    {type.name}
                                                </option>
                                            ))}
                                        </Input>
                                    </FormGroup>

                                    <FormGroup>
                                        <Label>Year</Label>
                                        <DatePicker
                                            selected={examYear}
                                            onChange={handleYearChange}
                                            showYearPicker
                                            dateFormat="yyyy"
                                            yearItemNumber={9}
                                            className="form-control"
                                            placeholderText="Select Year"
                                        />
                                    </FormGroup>

                                    {subjects.length > 0 && (
                                        <>
                                            <h4>Exam Schedule</h4>
                                            {examSchedule.map((schedule, index) => (
                                                <Card key={schedule.subjectId} className="mb-3">
                                                    <CardBody>
                                                        <h5>{schedule.subjectName} ({schedule.subjectCode})</h5>
                                                        <p>Staff: {schedule.teacherName}</p>
                                                        <FormGroup>
                                                            <Label>Date</Label>
                                                            <Input
                                                                type="date"
                                                                value={schedule.examDate || ''}
                                                                onChange={(e) => handleScheduleChange(index, 'examDate', e.target.value)}
                                                                required
                                                            />
                                                        </FormGroup>
                                                        <FormGroup>
                                                            <Label>Start Time</Label>
                                                            <Input
                                                                type="time"
                                                                value={schedule.startTime || ''}
                                                                onChange={(e) => handleScheduleChange(index, 'startTime', e.target.value)}
                                                                required
                                                            />
                                                        </FormGroup>
                                                        <FormGroup>
                                                            <Label>End Time</Label>
                                                            <Input
                                                                type="time"
                                                                value={schedule.endTime || ''}
                                                                onChange={(e) => handleScheduleChange(index, 'endTime', e.target.value)}
                                                                required
                                                            />
                                                        </FormGroup>
                                                    </CardBody>
                                                </Card>
                                            ))}
                                        </>
                                    )}

                                    <Button color="secondary" type="submit" disabled={loading}>
                                        {loading ? <Spinner size="sm" /> : 'Save as Draft'}
                                    </Button>
                                    <Button color="primary" type="submit" disabled={loading} onClick={(e) => handleSubmit(e, 'approved')} style={{ marginLeft: "10px" }}>
                                        {loading ? <Spinner size="sm" /> : 'Publish'}
                                    </Button>
                                </Form>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <Footer />
            </Container>

        </>
    );
}

export default AddExam;
